import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { getPlanCta } from "@/lib/planCta";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Layers, Camera, Check, X, RotateCw, Lock, ChevronRight, GitCompareArrows, Loader2, Ban, Sparkles } from "lucide-react";

const STATUS = {
  no_match: { label: "No matching photo yet", cls: "bg-[#F1F5F9] text-[#64748B]" },
  similar_view_captured: { label: "Similar view captured", cls: "bg-[#DBEAFE] text-[#1E40AF]" },
  skipped: { label: "Skipped — no longer applicable", cls: "bg-[#FEF3C7] text-[#92400E]" },
};

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export function ProofReplay({ propertyId, archived }) {
  const navigate = useNavigate();
  const { isPremium, trialing, trialEligible, openUpgrade } = useTier();
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [err, setErr] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState(null); // {baseline, roomId} capture in progress
  const [pending, setPending] = useState(null); // {baseline, moPhoto} awaiting confirm
  const [busy, setBusy] = useState(false);
  const [rooms, setRooms] = useState([]);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [{ data: d }, { data: prop }] = await Promise.all([
        api.get(`/properties/${propertyId}/proof-replay`),
        api.get(`/properties/${propertyId}`),
      ]);
      if (!d || !Array.isArray(d.rooms) || !d.progress) throw new Error("malformed");
      setData(d);
      setRooms(prop.rooms || []);
      setErr(false); setEmpty(false); setLocked(false);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 402) setLocked(true);            // TenantSeal Plus lock
      else if (st === 400) setEmpty(true);         // no linked sealed baseline yet
      else setErr(true);                           // network / 5xx / malformed / unexpected 4xx
    } finally { setLoaded(true); }
  }, [propertyId]);

  const retry = () => { setErr(false); setLoaded(false); load(); };

  useEffect(() => { load(); }, [load]);

  const startMatch = (baseline) => {
    const room = rooms.find((r) => r.name === baseline.room);
    if (!room) { toast.error(`Add a "${baseline.room}" room to your move-out first.`); return; }
    setActive({ baseline, roomId: room.room_id });
    setTimeout(() => fileRef.current?.click(), 50);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || !active) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("client_upload_id", uid()); fd.append("import_mode", "live");
      const { data: p } = await api.post(`/rooms/${active.roomId}/photos`, fd);
      setPending({ baseline: active.baseline, moPhoto: { photo_id: p.photo_id, storage_path: p.storage_path } });
      setActive(null);
    } catch { toast.error("That photo didn't upload — please try again."); setActive(null); }
    finally { setBusy(false); }
  };

  const confirmPair = async (replace = false) => {
    setBusy(true);
    try {
      await api.post(`/properties/${propertyId}/proof-replay/pair`, {
        baseline_photo_id: pending.baseline.baseline_photo_id, move_out_photo_id: pending.moPhoto.photo_id, replace,
      });
      toast.success("Pair confirmed."); setPending(null); load();
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm("This baseline already has a matched photo. Replace it? The earlier photo is kept, just unpaired.")) return confirmPair(true);
      } else toast.error(e?.response?.data?.detail || "Couldn't confirm the pair.");
    } finally { setBusy(false); }
  };

  const retake = async () => {
    // discard the just-uploaded photo so no orphan/unintended pairing remains
    try { await api.delete(`/photos/${pending.moPhoto.photo_id}`); } catch { /* noop */ }
    const b = pending.baseline; setPending(null); startMatch(b);
  };

  const keepUnpaired = () => { toast.info("Photo kept in this room, not paired."); setPending(null); load(); };

  const toggleSkip = async (baseline, skip) => {
    try {
      await api.post(`/properties/${propertyId}/proof-replay/skip`, { baseline_photo_id: baseline.baseline_photo_id, skip });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update."); }
  };

  if (!loaded) return null;
  const cta = getPlanCta({ trialEligible, trialing });

  if (locked) {
    return (
      <div data-testid="proof-replay-locked" className="rounded-2xl border border-brand/20 bg-brand/[0.04] p-5 theme-card">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><Layers size={20} /></span>
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-bold text-navy flex items-center gap-2">Proof Replay <Lock size={14} className="text-[#94A3B8]" /></h3>
            <p className="mt-0.5 text-sm text-[#64748B]">Use your sealed baseline as a guide so your move-out photos cover similar views.</p>
            <Button data-testid="proof-replay-upgrade" onClick={() => openUpgrade("proof_replay")}
              className="mt-3 h-10 rounded-xl bg-navy hover:bg-navy-hover text-white">
              <Sparkles size={15} className="mr-2" /> {cta.cta}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  if (err) {
    return (
      <div data-testid="proof-replay-error" className="rounded-2xl border border-[#E2E8F0] bg-white p-5 theme-card">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-[#FEF2F2] text-[#DC2626] shrink-0"><Layers size={20} /></span>
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-bold text-navy">Proof Replay</h3>
            <p className="mt-0.5 text-sm text-[#64748B]">Proof Replay couldn’t load right now.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button data-testid="proof-replay-retry" onClick={retry}
                className="h-10 rounded-xl bg-navy hover:bg-navy-hover text-white">
                <RotateCw size={15} className="mr-1.5" /> Retry
              </Button>
              <Button data-testid="proof-replay-error-records" variant="outline" onClick={() => navigate("/dashboard")}
                className="h-10 rounded-xl border-[#E2E8F0] text-navy hover:bg-[#F8FAFC]">
                <ChevronRight size={15} className="mr-1.5" /> Return to records
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (empty) {
    return (
      <div data-testid="proof-replay-empty" className="rounded-2xl border border-[#E2E8F0] bg-white p-5 theme-card">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><Layers size={20} /></span>
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-bold text-navy">Proof Replay</h3>
            <p className="mt-0.5 text-sm text-[#64748B]">Proof Replay needs a sealed earlier record before matching views.</p>
            <Button data-testid="proof-replay-find-baseline" variant="outline" onClick={() => navigate("/dashboard")}
              className="mt-3 h-10 rounded-xl border-[#E2E8F0] text-navy hover:bg-[#F8FAFC]">
              <ChevronRight size={15} className="mr-1.5" /> Go to your records
            </Button>
          </div>
        </div>
      </div>
    );
  }
  if (!data) return null;
  const { progress } = data;

  return (
    <div data-testid="proof-replay" className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6 theme-card">
      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="proof-replay-file-input" />
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><Layers size={20} /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-lg font-bold text-navy">Proof Replay — match your earlier photos</h3>
          <p className="mt-0.5 text-sm text-[#64748B] leading-snug">Use your sealed baseline as a guide so your move-out photos cover similar views.</p>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <span data-testid="proof-replay-progress" className="text-sm font-semibold text-navy">
          {progress.paired} of {progress.total} baseline views recaptured · {progress.skipped} skipped
        </span>
        <Button data-testid="proof-replay-run-comparison" variant="outline" onClick={() => navigate(`/compare/${propertyId}`)}
          className="h-9 rounded-xl border-[#E2E8F0] text-navy hover:bg-[#F8FAFC]" disabled={progress.paired === 0}>
          <GitCompareArrows size={14} className="mr-1.5" /> Run comparison
        </Button>
      </div>

      {data.rooms.map((rm) => (
        <div key={rm.room} className="mt-4">
          <p className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">{rm.room}</p>
          <div className="mt-2 space-y-2.5">
            {rm.items.map((it) => {
              const st = STATUS[it.status] || STATUS.no_match;
              return (
                <div key={it.baseline_photo_id} data-testid={`replay-item-${it.baseline_photo_id}`}
                  className="rounded-xl border border-[#E2E8F0] bg-white p-3 flex items-center gap-3">
                  <img src={fileUrl(it.storage_path)} alt="baseline" className="h-14 w-14 rounded-lg object-cover border border-[#E2E8F0] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${st.cls}`}>{st.label}</span>
                    {it.caption && <p className="mt-1 text-xs text-[#475569] truncate">{it.caption}</p>}
                    <p className="text-[10px] text-[#94A3B8]">Earlier sealed photo · {new Date(it.captured_at).toLocaleDateString()}</p>
                  </div>
                  {!archived && (
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {it.status !== "skipped" && (
                        <button data-testid={`replay-match-${it.baseline_photo_id}`} onClick={() => startMatch(it)}
                          className="inline-flex items-center gap-1 rounded-lg bg-brand hover:bg-brand-hover text-white text-xs font-semibold px-2.5 py-1.5">
                          <Camera size={13} /> {it.status === "similar_view_captured" ? "Redo" : "Match this view"}
                        </button>
                      )}
                      <button data-testid={`replay-skip-${it.baseline_photo_id}`} onClick={() => toggleSkip(it, it.status !== "skipped")}
                        className="inline-flex items-center gap-1 text-[11px] text-[#94A3B8] hover:text-navy">
                        {it.status === "skipped" ? <><RotateCw size={11} /> Unskip</> : <><Ban size={11} /> Skip</>}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Side-by-side confirm after native capture */}
      {pending && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="replay-confirm-modal" onClick={() => keepUnpaired()}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h4 className="font-heading text-lg font-bold text-navy">Same view?</h4>
              <button onClick={() => keepUnpaired()} className="text-[#94A3B8] hover:text-navy"><X size={18} /></button>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div><p className="text-[10px] font-semibold text-[#94A3B8] mb-1">Earlier sealed photo</p>
                <img src={fileUrl(pending.baseline.storage_path)} alt="baseline" className="aspect-square w-full object-cover rounded-lg border border-[#E2E8F0]" /></div>
              <div><p className="text-[10px] font-semibold text-[#94A3B8] mb-1">New move-out photo</p>
                <img src={fileUrl(pending.moPhoto.storage_path)} alt="new" className="aspect-square w-full object-cover rounded-lg border border-[#E2E8F0]" /></div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button data-testid="replay-confirm-pair" onClick={() => confirmPair(false)} disabled={busy}
                className="h-10 rounded-xl bg-navy hover:bg-navy-hover text-white">
                {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Check size={15} className="mr-2" />} Confirm pair
              </Button>
              <Button data-testid="replay-retake" variant="outline" onClick={retake} disabled={busy}
                className="h-10 rounded-xl border-[#E2E8F0] text-navy hover:bg-[#F8FAFC]"><RotateCw size={15} className="mr-2" /> Retake</Button>
              <Button data-testid="replay-keep-unpaired" variant="ghost" onClick={keepUnpaired} disabled={busy}
                className="h-10 rounded-xl text-[#64748B]">Keep without pairing</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
